import { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from './firebase';
import {
  onAuthStateChanged, signOut, signInWithPopup, signInWithRedirect, GoogleAuthProvider,
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signInAnonymously, updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';

export function formatDisplayName(fullName) {
  if (!fullName) return 'Player';
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
}
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const ref = doc(db, 'users', firebaseUser.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setProfile(snap.data());
          onSnapshot(ref, (s) => { if (s.exists()) setProfile(s.data()); });
        } else {
          const newProfile = {
            displayName: firebaseUser.displayName || 'Player',
            email: firebaseUser.email || null,
            tier: 'free',
            createdAt: serverTimestamp(),
            stats: { gamesPlayed: 0, wins: 0, bestRoundScore: null },
            cosmetics: { activeSkin: 'classic', activeTable: 'green-felt', owned: [] }
          };
          await setDoc(ref, newProfile);
          setProfile(newProfile);
        }
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

const loginWithGoogle = async () => {
  const isWebView = window.navigator.userAgent.includes('wv') || 
                    typeof window.Android !== 'undefined';
  if (isWebView) {
    throw new Error('Google Sign-In not available in app. Please use email/password.');
  }
  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    return await signInWithPopup(auth, provider);
  } catch (e) {
    if (e.code === 'auth/popup-blocked') {
      return await signInWithRedirect(auth, new GoogleAuthProvider());
    }
    throw e;
  }
};
  const loginWithEmail = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

 const signupWithEmail = async (email, password, displayName) => {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  const formatted = formatDisplayName(displayName);
  await updateProfile(result.user, { displayName: formatted });
  return result;
};

  const loginAnonymously = () => signInAnonymously(auth);

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      loginWithGoogle,
      loginWithEmail,
      signupWithEmail,
      loginAnonymously,
      logout
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
